import { FC } from 'react'

import { Box, Tooltip } from '@mui/material'

import { clickableProps } from '../../helpers/interactive'
import { GAME_ICON_RADIUS, focusRingSx, gameIconSx } from '../../helpers/styles'
import { useUAInsightsActions } from '../../hooks/useUAInsightsNavigation'
import type { GameRef } from '../../types'

interface ShowcaseGameIconsProps {
  games: GameRef[]
  /** Max icons to render. Defaults to 3. */
  max?: number
  /** Icon edge length in px. Defaults to 28. */
  size?: number
}

/**
 * Row of clickable game-icon blocks for the showcase "top games" cells. Each
 * icon opens that game's page inside UA Insights; click propagation is stopped
 * so the icons work even inside a clickable pattern row / card. Falls back to
 * the game's initial when no icon URL is available.
 */
export const ShowcaseGameIcons: FC<ShowcaseGameIconsProps> = ({ games, max = 3, size = 28 }) => {
  const { openGame } = useUAInsightsActions()

  const visible = games.slice(0, max)
  if (!visible.length) return null

  return (
    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
      {visible.map((game) => (
        <Tooltip key={game.id} title={game.name} arrow>
          {game.icon ? (
            <Box
              component="img"
              src={game.icon}
              alt={game.name}
              loading="lazy"
              decoding="async"
              {...clickableProps(() => openGame(game.id), { stopPropagation: true })}
              sx={{
                ...gameIconSx(size),
                cursor: 'pointer',
                transition: 'transform 0.1s ease, box-shadow 0.1s ease',
                '&:hover': { transform: 'translateY(-1px)', boxShadow: 2 },
                ...focusRingSx(),
              }}
            />
          ) : (
            <Box
              aria-label={game.name}
              {...clickableProps(() => openGame(game.id), { stopPropagation: true })}
              sx={{
                width: size,
                height: size,
                borderRadius: GAME_ICON_RADIUS,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'action.hover',
                color: 'text.secondary',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                cursor: 'pointer',
                flexShrink: 0,
                fontSize: '0.75rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                transition: 'transform 0.1s ease, box-shadow 0.1s ease',
                '&:hover': { transform: 'translateY(-1px)', boxShadow: 2 },
                ...focusRingSx(),
              }}
            >
              {game.name.charAt(0)}
            </Box>
          )}
        </Tooltip>
      ))}
    </Box>
  )
}
